import React, { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchEcacArrivalSummaryReport,
    patchBookings,
    resetEcacArrivalSummaryReport,
    resetPatchBookingStatus,
    selectEcacArrivalSummaryReportData,
    selectEcacArrivalSummaryReportStatus,
    selectEcacArrivalSummaryReportError,
    selectPatchBookingStatus,
} from '_redux/slices/sla-reports/ecac-arrival-summary-report';
import { selectFirebaseUser } from '_redux/slices/main-screen/user';
import { setToast, setShowToast } from '_redux/slices/timeline/timeline-toast';
import ReportHeader from './report-header';
import EcacArrivalSummaryTable from './ecac-arrival-summary-table';
import EcacArrivalSummaryRawTable from './ecac-arrival-summary-raw-table';

// bootstrap
import { Col, Row } from 'react-bootstrap';

/**
 * ECAC Arrival Summary Report - Displays ECAC arrival summary metrics for the selected date range and IATA.
 * @param {Object} props
 * @param {Array} props.dateRange - [startDate, endDate] for the report
 * @param {string} props.iata - IATA code to filter by
 */
const EcacArrivalSummaryReport = ({ dateRange, iata, runReportRef, onRunLoadingChange, hideBreakdown }) => {
    const [overrideRows, setOverrideRows] = useState(null);
    const dispatch = useDispatch();
    const firebaseUser = useSelector(selectFirebaseUser);
    const data = useSelector(selectEcacArrivalSummaryReportData);
    const status = useSelector(selectEcacArrivalSummaryReportStatus);
    const error = useSelector(selectEcacArrivalSummaryReportError);
    const patchStatus = useSelector(selectPatchBookingStatus);
    const isLoading = status === 'loading';
    const isPatching = patchStatus === 'loading';

    const handleRunReport = useCallback(() => {
        const startDate = dateRange?.[0];
        const endDate = dateRange?.[1];

        if (!startDate || !endDate || !iata) return;

        const toLocalDateString = (d) => { const dt = new Date(d); return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`; };
        const formattedStartDate = toLocalDateString(startDate);
        const formattedEndDate = toLocalDateString(endDate);

        dispatch(
            fetchEcacArrivalSummaryReport({
                startDate: formattedStartDate,
                endDate: formattedEndDate,
                iata,
                hideBreakdown,
            })
        );
    }, [dispatch, dateRange, iata, hideBreakdown]);

    useEffect(() => {
        return () => {
            dispatch(resetEcacArrivalSummaryReport());
        };
    }, [dispatch]);

    useEffect(() => {
        if (patchStatus === 'succeeded') {
            dispatch(setToast({ message: 'Booking(s) updated successfully', statusType: 'success' }));
            dispatch(setShowToast(true));
            dispatch(resetPatchBookingStatus());
            setOverrideRows(null);
            const toLocalDateString = (d) => { const dt = new Date(d); return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`; };
            const startDate = dateRange?.[0] ? toLocalDateString(dateRange[0]) : null;
            const endDate = dateRange?.[1] ? toLocalDateString(dateRange[1]) : null;
            if (startDate && endDate && iata) {
                dispatch(fetchEcacArrivalSummaryReport({ startDate, endDate, iata, hideBreakdown }));
            }
        }
    }, [patchStatus, dispatch, dateRange, iata, hideBreakdown]);

    useEffect(() => {
        if (patchStatus === 'failed') {
            dispatch(setToast({ message: 'Failed to update booking(s)', statusType: 'failed' }));
            dispatch(setShowToast(true));
            dispatch(resetPatchBookingStatus());
        }
    }, [patchStatus, dispatch]);

    useEffect(() => {
        if (runReportRef) runReportRef.current = handleRunReport;
        return () => { if (runReportRef) runReportRef.current = null; };
    }, [handleRunReport, runReportRef]);

    useEffect(() => {
        if (onRunLoadingChange) onRunLoadingChange(isLoading);
    }, [isLoading, onRunLoadingChange]);

    return (
        <Row>
            <ReportHeader onRunReport={handleRunReport} isLoading={isLoading} hideRunButton={!!runReportRef} />

            {status === 'failed' && (
                <p style={{ color: '#991b1b', fontSize: '13px' }}>
                    Error loading ECAC Arrival Summary report: {error}
                </p>
            )}

            {data?.summary && (
                <Row>
                    <Col xs={hideBreakdown ? 12 : 4}>
                        <EcacArrivalSummaryTable summary={data.summary} startDate={data.startDate} endDate={data.endDate} />
                    </Col>
                    {!hideBreakdown && (
                        <Col xs={8}>
                            <EcacArrivalSummaryRawTable
                                rawData={data.rawData}
                                iata={iata}
                                overrideRows={overrideRows}
                                onOverrideRowsChange={setOverrideRows}
                                onPatchBookings={(rows, slaBand, reason) => {
                                    const bookingIds = (rows || []).map((row) => row?.bookingId).filter(Boolean);
                                    if (bookingIds.length && reason) {
                                        dispatch(patchBookings({
                                            bookingIds,
                                            patch: {
                                                ecacReportOverride: {
                                                    slaBand,
                                                    reason,
                                                    userId: firebaseUser?.uid ?? null,
                                                    userName: firebaseUser?.displayName ?? firebaseUser?.email ?? 'Unknown',
                                                    email: firebaseUser?.email ?? null,
                                                    timestamp: new Date().toISOString(),
                                                },
                                            },
                                        }));
                                    }
                                }}
                                isPatching={isPatching}
                            />
                        </Col>
                    )}
                </Row>
            )}
        </Row>
    );
};

export default EcacArrivalSummaryReport;
